import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getMyWeeklyRankingRequest, getMyWeeklyRanking } from "../../../../redux/ranking";
import { getUser } from "../../../../redux/user";
import { TEMPLATE_ID } from "../../../../api/constants";
import { MatchesList } from "../";
import Countdown from "../Countdown/Countdown";
import Points from "../Points/Points";

/**
 * Component for displaying past gameweeks with results or missed deadline
 * @param {Object} props - Component props
 * @param {Array} props.filteredMatches - Matches for the past gameweek
 * @param {Array} props.availableMarkets - Available markets for predictions
 * @param {Array} props.summary - Match summaries
 * @param {Array} props.userPredictions - User's existing predictions
 * @param {Object} props.currentScores - Current scores for matches
 * @param {Object} props.fansUnitedSDK - FansUnited SDK instance
 * @param {boolean} props.hasPredictionsForCurrentMatches - Whether user made predictions for this gameweek
 * @param {number} props.currentPoints - Points earned for this gameweek
 * @param {string} props.deadline - Deadline string
 * @param {string} props.selectedGroupId - The selected gameweek/group ID
 * @returns {JSX.Element} - Past gameweek component
 */
const PastGameweek = ({
	filteredMatches,
	availableMarkets,
	summary,
	userPredictions,
	currentScores,
	fansUnitedSDK,
	hasPredictionsForCurrentMatches,
	currentPoints,
	deadline,
	selectedGroupId,
}) => {
	const dispatch = useDispatch();
	const user = useSelector(getUser);
	const myWeeklyRankingList = useSelector(getMyWeeklyRanking);
	const [isLoadingWeeklyPosition, setIsLoadingWeeklyPosition] = useState(false);

	// Extract weekly position from Redux state
	const weeklyPosition = myWeeklyRankingList?.filter((template) => template.id === TEMPLATE_ID)[0]?.position;

	// Fetch weekly position when component mounts or selectedGroupId changes
	useEffect(() => {
		if (!fansUnitedSDK || !selectedGroupId || !user?.id) {
			return;
		}

		setIsLoadingWeeklyPosition(true);

		dispatch(getMyWeeklyRankingRequest(fansUnitedSDK, selectedGroupId, user.id))
			.then(() => {
				setIsLoadingWeeklyPosition(false);
			})
			.catch(() => {
				setIsLoadingWeeklyPosition(false);
			});
	}, [dispatch, fansUnitedSDK, selectedGroupId, user?.id]);
	return (
		<>
			<Points currentPoints={currentPoints ?? 0} weeklyPosition={weeklyPosition} isLoadingWeeklyPosition={isLoadingWeeklyPosition} />

			{hasPredictionsForCurrentMatches ? (
				<MatchesList
					matches={filteredMatches}
					availableMarkets={availableMarkets}
					summary={summary}
					userPredictions={userPredictions}
					currentScores={currentScores}
					onScoreSet={() => {}} // No-op for past rounds
					fansUnitedSDK={fansUnitedSDK}
					canMakePredictions={false} // Never allow predictions for past rounds
				/>
			) : (
				<Countdown
					initialTimeLeft={{ days: 0, hours: 0, minutes: 0, seconds: 0 }}
					deadline={deadline}
					showDeadlineMissed={true}
					deadlineMissedTitle="Matchweek Deadline"
					deadlineMissedMessage="You didn't make your predictions for this week, so no points were earned - but there's always next week to bounce back!"
				/>
			)}
		</>
	);
};

export default PastGameweek;
